// Tool Manager — ALL external calls go through here.
// Agents never call APIs directly. Built-in: caching, retry, fallback.

import { apiUrl } from '../services/apiBase';

// ─── In-memory result cache (cleared on page refresh) ─────────────────────────
const _cache = new Map();

function fromCache(key, ttlSec) {
  const entry = _cache.get(key);
  if (!entry) return null;
  if ((Date.now() - entry.ts) > ttlSec * 1000) { _cache.delete(key); return null; }
  return entry.value;
}
function toCache(key, value) {
  _cache.set(key, { value, ts: Date.now() });
}

// ─── Retry wrapper ────────────────────────────────────────────────────────────
async function withRetry(fn, retries = 2) {
  for (let i = 0; i <= retries; i++) {
    try { return await fn(); }
    catch (e) {
      if (i === retries) throw e;
      await new Promise(r => setTimeout(r, 400 * (i + 1)));
    }
  }
}

export const ToolManager = {

  // ── Groq Chat (via our Express proxy at /api/chat) ────────────────────────
  // cacheKey + cacheTTL: if provided, result is cached in memory
  async callGroq(messages, systemPrompt = '', maxTokens = 800, cacheKey = null, cacheTTL = 0) {
    if (cacheKey && cacheTTL > 0) {
      const hit = fromCache(cacheKey, cacheTTL);
      if (hit) return { text: hit, fromCache: true };
    }

    const text = await withRetry(async () => {
      const res = await fetch(apiUrl('/api/chat'), {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ system: systemPrompt, messages, max_tokens: maxTokens }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Groq HTTP ${res.status}`);
      }
      const data = await res.json();
      return data.content?.[0]?.text || '';
    });

    if (cacheKey && cacheTTL > 0) toCache(cacheKey, text);
    return { text, fromCache: false };
  },

  // ── Groq Vision (llama-4-scout via /api/vision) ───────────────────────────
  async callVision(base64Image, question, mimeType = 'image/png') {
    const res = await fetch('/api/vision', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ image: base64Image, mimeType, question }),
    });
    if (!res.ok) throw new Error('Vision API failed');
    const data = await res.json();
    if (data.error) throw new Error(data.error);
    return data.text || '';
  },

  // ── Yahoo Finance quote (via /api/india/quote) ────────────────────────────
  async getQuote(symbol, ttlSec = 30) {
    const key = `quote_${symbol}`;
    const hit = fromCache(key, ttlSec);
    if (hit) return { ...hit, fromCache: true };

    const res = await fetch(`/api/india/quote?symbol=${encodeURIComponent(symbol)}`);
    if (!res.ok) throw new Error(`Quote failed for ${symbol}`);
    const data = await res.json();
    toCache(key, data);
    return { ...data, fromCache: false };
  },

  // ── Yahoo Finance OHLCV history (via /api/india/history) ─────────────────
  async getHistory(symbol, period = '1mo', ttlSec = 300) {
    const key = `hist_${symbol}_${period}`;
    const hit = fromCache(key, ttlSec);
    if (hit) return { data: hit, fromCache: true };

    const res = await fetch(`/api/india/history?symbol=${encodeURIComponent(symbol)}&period=${period}`);
    if (!res.ok) throw new Error(`History failed for ${symbol}`);
    const data = await res.json();
    toCache(key, data);
    return { data, fromCache: false };
  },

  // ── Simple commodity/forex quote — uses /api/price (NOT /api/india) ─────────
  async getPriceQuote(symbol) {
    const key = `pq_${symbol}`;
    const hit = fromCache(key, 30);
    if (hit) return { ...hit, fromCache: true };
    const res = await fetch(`/api/price/quote?symbol=${encodeURIComponent(symbol)}`);
    if (!res.ok) throw new Error(`Price quote failed for ${symbol}`);
    const data = await res.json();
    toCache(key, data);
    return { ...data, fromCache: false };
  },

  // ── XAU/USD Gold price (special handler with fallback) ────────────────────
  // STALENESS CHECK — found live 2026-08-23 (TradingView showed ~4603 while
  // Jarvis's Home tile showed 4680.60 — a ~$77 gap Sir spotted himself).
  // Root cause traced with direct Yahoo API calls: GC=F's ENTIRE feed
  // (both the summary quote AND its newest candle) had frozen at the exact
  // same timestamp for 1-2 days straight — a genuine Yahoo-side outage/
  // contract-rollover gap, not bad math in this file. But Jarvis had no way
  // to notice and kept confidently showing the frozen number as if live.
  // Fix: each candidate symbol's own reported `asOf` timestamp is now
  // checked — anything older than STALE_MINUTES is skipped in favor of the
  // next symbol in the fallback chain, and if EVERY symbol comes back
  // stale, the freshest one is still returned but marked `stale: true` +
  // `dataAgeMinutes` so the UI can warn instead of lying by omission.
  async getGoldPrice() {
    const key = 'gold_price';
    const hit = fromCache(key, 30);
    if (hit) return { ...hit, fromCache: true };

    const STALE_MINUTES = 20;
    let staleFallback = null; // best (freshest) stale candidate, used only if nothing fresh is found

    // PAXG-USD (Pax Gold) FIRST — found live 2026-08-23. Sir compared
    // Jarvis's price tile against TradingView's FOREX.com spot/CFD chart
    // and asked for the same spot price. TradingView's own feed isn't
    // freely fetchable (no public API, scraping it would violate their
    // ToS), and Yahoo's proper spot symbol (XAUUSD=X) is delisted/404s now
    // — confirmed directly, along with every other Yahoo spot-gold ticker
    // variant tried (XAU=X, XAUUSD, ^XAU, etc., none work or aren't spot
    // gold at all). PAXG is the practical fix: it's a REAL, NYDFS-regulated,
    // redeemable gold-backed token (1 token = 1 troy oz, held at Brink's) —
    // arbitrage keeps its market price tracking physical spot gold tightly,
    // confirmed live matching TradingView's ~4603 almost exactly while
    // GC=F (futures) showed 4680.60. Bonus: crypto trades 24/7, so unlike
    // GC=F it never goes stale over the weekend either. GC=F/GLD stay as
    // fallbacks only if PAXG itself is ever unavailable.
    for (const sym of ['PAXG-USD', 'GC=F', 'XAUUSD=X', 'GLD']) {
      try {
        const q = await this.getPriceQuote(sym);
        if (q.price && q.price > 100) {         // sanity check — gold is >$100
          const ageMin = q.asOf ? (Date.now() - new Date(q.asOf).getTime()) / 60000 : null;
          const result = {
            price:     +q.price.toFixed(2),
            change:    +(q.change  || 0).toFixed(2),
            changePct: +(q.changePct || 0).toFixed(2),
            high:      +(q.high   || q.price).toFixed(2),
            low:       +(q.low    || q.price).toFixed(2),
            open:      +(q.open   || q.price).toFixed(2),
            volume:    q.volume   || 0,
            symbol:    'XAU/USD',
            source:    sym,
            asOf:      q.asOf || null,
            dataAgeMinutes: ageMin != null ? +ageMin.toFixed(1) : null,
            stale:     ageMin != null ? ageMin > STALE_MINUTES : false,
          };
          if (!result.stale) {
            toCache(key, result);
            return { ...result, fromCache: false };
          }
          // Stale — keep it as a fallback candidate (prefer the freshest
          // one seen) but keep trying other symbols for a fresh hit first.
          if (!staleFallback || (ageMin != null && ageMin < staleFallback.dataAgeMinutes)) {
            staleFallback = result;
          }
        }
      } catch { /* try next */ }
    }
    if (staleFallback) {
      // Every symbol was stale — better to show it clearly marked than to
      // throw and lose the price tile entirely. Short cache TTL (not the
      // normal 30s) so it re-checks for a fresh feed sooner.
      toCache(key, staleFallback);
      return { ...staleFallback, fromCache: false };
    }
    throw new Error('Could not fetch gold price from Yahoo Finance');
  },

  // ── XAU/USD OHLCV candles ────────────────────────────────────────────────
  // PAXG-USD first — Sir's explicit call (2026-08-23): the app trades a
  // CFD, so the price everywhere in Jarvis (ticker AND the candles the
  // signal engine reads) must match what he actually sees/trades on
  // TradingView, not a mix of two different sources. Quick 3-month
  // walk-forward comparison against GC=F on the SAME window found no red
  // flag (PAXG: 5 trades/+1.43R, GC=F: 3 trades/+1.09R) — but both counts
  // are far below the project's own 30-trade confidence bar, so this is
  // "nothing alarming found", not "proven equivalent". Watch
  // JarvisMemory → Patterns / the Backtest screen going forward the same
  // way any other live signal is watched. GC=F stays as an automatic
  // fallback (e.g. if a requested period needs more history than PAXG's
  // ~3mo of hourly / ~1y of daily depth on Yahoo actually has).
  async getGoldCandles(period = '1mo', interval = '1d') {
    const key = `gold_candles_${period}_${interval}`;
    const hit = fromCache(key, 300);
    if (hit) return { candles: hit, fromCache: true };

    const MIN_USABLE = 20; // fewer than this and it's not enough to analyze — try the fallback symbol instead
    let candles = [];
    for (const sym of ['PAXG-USD', 'GC=F']) {
      try {
        candles = await this._fetchCandleHistory(sym, period, interval);
        if (candles.length >= MIN_USABLE) break;
      } catch { /* try next */ }
    }
    toCache(key, candles);
    return { candles, fromCache: false };
  },

  async _fetchCandleHistory(symbol, period, interval) {
    const res = await fetch(apiUrl(`/api/price/history?symbol=${encodeURIComponent(symbol)}&period=${period}&interval=${interval}`));
    if (!res.ok) throw new Error('Gold candle history fetch failed');
    const data = await res.json();
    // Normalize: array of { date, open, high, low, close, volume }
    return (Array.isArray(data) ? data : data?.quotes || [])
      .filter(c => c.high && c.low && c.open && c.close)
      .map(c => ({
        date:   c.date || c.timestamp,
        open:   +c.open.toFixed(2),
        high:   +c.high.toFixed(2),
        low:    +c.low.toFixed(2),
        close:  +c.close.toFixed(2),
        volume: c.volume || 0,
      }));
  },

  // ── PDF text extraction (via /api/learn/pdf) ──────────────────────────────
  async extractPDF(base64) {
    const res = await fetch('/api/learn/pdf', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ base64 }),
    });
    if (!res.ok) throw new Error('PDF extraction failed');
    const data = await res.json();
    return { text: data.text || '', pages: data.pages || 0 };
  },

  // ── Cache utilities ───────────────────────────────────────────────────────
  clearCache()    { _cache.clear(); },
  cacheSize()     { return _cache.size; },
  cacheKeys()     { return Array.from(_cache.keys()); },
};
